import { UnitSystem } from '~/_shared/types/googleMaps/googleMaps.types';
import type { LatLng } from '~/_shared/types/latLng';
import { convertDistanceToUnitSystem } from '~/_shared/utils/distance/distance.helpers';
import {
  type AgsReportRingDataRequest, type AgsReportRingDataResponse, type ReportAreaResponse,
} from '~/exportReport/exportReport.repository';
import type { ReportTableColumn } from '~/exportReport/table/ReportTable.model';
import type {
  ReportTemplate, ReportTemplateDataRow,
} from '~/exportReport/template/reportTemplate';

export type ReportDemographicData = {
  id: string;
  label: string;
  value: number | string |null;
};

export const normalizeReportDemographicId = (demographicId: string): string => demographicId.replace('%', '');

export const getReportTemplateDataRowDemographicId = (row: ReportTemplateDataRow): string => {
  return row.id.startsWith('%') && row.pct_id ? row.pct_id : row.id;
};

export const getReportTemplateDemographicIds = (template: ReportTemplate): ReadonlyArray<string> => {
  return [...template.items.reduce<Set<string>>((demographicIds, item) => {
    if (item.type !== 'table') {
      return demographicIds;
    }

    item.content.forEach(row => {
      if (row.type !== 'data-row') {
        return;
      }

      demographicIds.add(getReportTemplateDataRowDemographicId(row));
    });

    return demographicIds;
  }, new Set())];
};

const getReportAreaData = (areas: ReadonlyArray<ReportAreaResponse>, columnId: string): AgsReportRingDataResponse['areas'][number] | undefined => {
  if (!areas?.length) {
    return undefined;
  }

  if (areas.length === 1) {
    return areas[0];
  }

  return areas.find(area => area.type === 'ring' && area.extent?.toString() === columnId);
};

export const getReportDemographicDataFromResults = (
  areas: ReadonlyArray<ReportAreaResponse>,
  columnId: string,
  demographicId: string
): ReportDemographicData | null => {
  const columnData = getReportAreaData(areas, columnId);
  if (!columnData || !columnData.data) {
    return null;
  }

  const resultData = columnData.data.find(demographic => demographic.tag === demographicId);
  const numericalValue = Number(resultData?.value);

  return resultData ? {
    value: isNaN(numericalValue) ? resultData?.value.toString() : numericalValue,
    label: resultData.description,
    id: demographicId,
  } : null;
};

export const getReportDemographicLabelFromResults = (
  areas: ReadonlyArray<ReportAreaResponse>,
  demographicId: string
): string | null => {
  const data = areas?.[0]?.data;
  if (!data) {
    return null;
  }

  const demographic = data.find(demographicData => normalizeReportDemographicId(demographicData.tag) === normalizeReportDemographicId(demographicId));
  return demographic?.description ?? null;
};

export const createAgsReportDataRingRequest = ({
  center, returnType, tableColumns, varlistId, country,
}: {
  center: LatLng;
  returnType?: AgsReportRingDataRequest['returnType'];
  tableColumns: ReadonlyArray<ReportTableColumn>;
  varlistId: string;
  country: 'usa' | 'can';
}): AgsReportRingDataRequest => {
  const radiiInMiles = tableColumns
    .filter(column => column.type === 'radius')
    .map(column => convertDistanceToUnitSystem({
      distance: column.distance,
      currentUnitSystem: column.unit,
      targetUnitSystem: UnitSystem.imperial,
    }))
    .filter(radius => !isNaN(radius));

  return {
    lat: center.lat,
    lng: center.lng,
    radiiInMiles,
    country,
    returnType,
    varlistId,
  };
};
